import React, { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { getHomeDashboardPath } from "../../../utils/dashboardRoutes";
import {
  hasActiveSubscription,
  isSubscriptionPurchaseRoute,
  SUBSCRIPTION_CHANGE_ROUTE,
} from "../../../utils/subscriptionStatus";

/**
 * SubscriptionRouteGuard
 *
 * Subscribed users: full access; redirected away from plan-purchase pages.
 * Unsubscribed users: dashboard + subscribe flow only; other routes → dashboard.
 */
export const SubscriptionRouteGuard = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const userData = useSelector((state) => state.auth.userData);
  const user = userData?.user;

  const isAdmin = String(user?.user_type || "").toLowerCase() === "admin";
  const isSubscribed = isAdmin || hasActiveSubscription(user);

  const allowedRoutesForUnsubscribed = useMemo(
    () => [
      "/dashboard",
      "/freelancers-tab",
      "/my-projects",
      "/project-proposals",
      "/projects",
      "/view-project",
      "/messages",
      "/subscribe",
      "/subscription",
      "/subscription-settings",
      SUBSCRIPTION_CHANGE_ROUTE,
      "/checkout",
      "/checkout-form",
      "/subscribe-checkout",
      "/register/freelancer/profile",
      "/profile",
      "/payments",
      "/stripe-onboard",
    ],
    [],
  );

  const isAllowedRoute = useCallback(
    (pathname) =>
      allowedRoutesForUnsubscribed.some(
        (route) => pathname === route || pathname.startsWith(`${route}/`),
      ),
    [allowedRoutesForUnsubscribed],
  );

  useEffect(() => {
    if (isAdmin) {
      navigate("/admin", { replace: true });
      return;
    }

    if (isSubscribed && isSubscriptionPurchaseRoute(location.pathname, location)) {
      navigate(getHomeDashboardPath(user), { replace: true });
      return;
    }

    if (!isSubscribed && !isAllowedRoute(location.pathname)) {
      navigate(getHomeDashboardPath(user), { replace: true });
    }
  }, [
    location,
    navigate,
    isSubscribed,
    isAdmin,
    user,
    isAllowedRoute,
  ]);

  if (isAdmin) {
    return null;
  }

  if (isSubscribed && isSubscriptionPurchaseRoute(location.pathname, location)) {
    return null;
  }

  if (!isSubscribed && !isAllowedRoute(location.pathname)) {
    return null;
  }

  return <>{children}</>;
};
